import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Avatar, Box, Button, ButtonBase, Card, Divider, Typography } from '@mui/material';
import { ordersPathTo } from '../utils/Api';
import { UserContext } from '../contexts/UserContext';
import { currency } from './building_blocks/Currency';
import ProductPhotoPlaceholder from '../images/placeholder_product_image.png';
import { apiBaseUrl } from '../utils/Api/Api';
import visa from '../images/visa_card.png';
import master from '../images/master_card.png';
import { OrderReview } from '../components';

/* Icons */
import { ArrowBackRounded } from '@mui/icons-material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { layoutButtonPillRadius, layoutCardRadius, layoutIconButtonRadius } from '../theme/colors';

const FieldInfo = ({ label, children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1, alignSelf: 'stretch' }}>
    <Typography variant="sectionTitle">
      {label}
    </Typography>

    {children}
  </Box>
);

const Price = ({ label, children }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', alignSelf: 'stretch' }}>
    <Typography sx={{ fontFamily: 'Nunito', fontSize: '14px', lineHeight: '24px', fontWeight: 400 }}>{label}</Typography>
    <Typography sx={{ fontFamily: 'Nunito', fontSize: '14px', lineHeight: '24px', fontWeight: 700 }}>{children}</Typography>
  </Box>
);

const OrderDetails = () => {
  const { currentUser } = useContext(UserContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const [order, setOrder] = useState({});
  const deliveryFee = 1000;

  const secondaryFont = {
    fontFamily: 'Nunito',
    fontSize: '14px',
    lineHeight: '24px'
  };

  const buttonStyle = {
    fontFamily: 'Nunito',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px'
  };

  const surfaceCardContainer = {
    display: 'flex',
    p: '24px',
    alignItems: 'flex-start',
    gap: 2,
    alignSelf: 'stretch',
    bgcolor: 'surface.card',
    borderRadius: layoutCardRadius,
    width: '100%'
  };

  const columnContainer = {
    display: 'flex',
    p: '16px 0',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2,
    alignSelf: 'stretch',
    borderRadius: layoutButtonPillRadius
  };

  const statusLabels = {
    open: 'Pedido pendente',
    pending: 'Pedido pendente',
    paid: 'Pedido confirmado',
    accepted: 'Pedido confirmado',
    courier: 'Em entrega',
    shipped: 'Entregue',
    done: 'Finalizado',
    failed: 'Pagamento falhou',
    rejected: 'Cancelado',
    canceled: 'Cancelado',
  };

  const formatOrderDate = () => {
    const dateMatch = order.date?.match(/^(\d{2})\/(\d{2})\/\d{2} às (\d{2}):(\d{2})/);
    const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

    if (dateMatch) {
      const [, day, month, hour, minute] = dateMatch;
      return `${day}/${monthNames[Number(month) - 1]} às ${hour}h${minute}`;
    }

    return order.date;
  };

  useEffect(() => {
    ordersPathTo.get(id).then(result => {
      setOrder(result.data);
      document.title = `Pedido #${result.data.id} - TudoPets.app`;
    });
  }, [id]);

  if (!order.provider) return null;

  const providerLogo = order.provider_business_logo && `${apiBaseUrl}${order.provider_business_logo}`;
  const providerNeighborhood = order.provider.address?.bairro;
  const providerName = providerNeighborhood ? `${order.provider.name} - ${providerNeighborhood}` : order.provider.name;
  const itemCount = order.order_items?.reduce((total, item) => total + Number(item.quantity || 0), 0) || 0;
  const statusLabel = statusLabels[order.status] || order.status;
  const paymentMethod = order.payment_method;
  const deliveryAddress = order.delivery_address || (
    currentUser?.default_address
      ? `${currentUser.default_address.logradouro}, ${currentUser.default_address.numero}`
      : ''
  );

  return (
    <Box sx={{ display: 'flex', p: '24px 16px 16px 16px', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
      {order.status === 'shipped' && <OrderReview setOrder={setOrder} order={order} />}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, alignSelf: 'stretch', position: 'relative' }}>
        <Button data-no-shadow="true" onClick={() => window.history.back()} sx={{ color: 'text.primary', borderRadius: layoutIconButtonRadius }}>
          <ArrowBackRounded />
        </Button>

        <Typography variant="pageTitle" sx={{ width: 'auto', transform: 'translateX(-50%)', position: 'absolute', left: '50%' }}>
          Seu pedido
        </Typography>
      </Box>

      <Card sx={{ ...surfaceCardContainer, alignItems: 'flex-start', gap: 2 }}>
        <Avatar src={providerLogo} sx={{ width: '40px', height: '40px' }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', alignSelf: 'stretch' }}>
            <Typography variant="storeName" sx={{ wordBreak: 'break-word' }}>
              {providerName}
            </Typography>

            <ButtonBase
              onClick={() => navigate(`/provider/${order.provider.id}`)}
              sx={{ bgcolor: 'surface.input', borderRadius: '50%', width: '40px', height: '40px', flexShrink: 0, color: '#90A4AE' }}
            >
              <MoreVertIcon />
            </ButtonBase>
          </Box>

          <Typography sx={{ color: '#546E7A', fontFamily: 'Nunito', fontSize: '14px', fontWeight: 400, lineHeight: '21px', wordBreak: 'break-word' }}>
            {formatOrderDate()} • {currency(order.total_amount)} ({itemCount} {itemCount === 1 ? 'item' : 'itens'})
          </Typography>

          <Box sx={{ alignItems: 'center', bgcolor: 'surface.muted', borderRadius: layoutCardRadius, display: 'flex', gap: 0.75, height: '26px', px: 1.5 }}>
            <Typography sx={{ color: '#546E7A', fontFamily: 'Nunito', fontSize: '14px', fontWeight: 400, lineHeight: '21px' }}>
              {statusLabel}
            </Typography>
            <InfoRoundedIcon sx={{ color: '#90A4AE', fontSize: '18px' }} />
          </Box>
        </Box>
      </Card>

      <Card sx={{ display: 'flex', p: '24px', flexDirection: 'column', alignItems: 'flex-start', gap: '7px', alignSelf: 'stretch', bgcolor: 'surface.card', borderRadius: layoutCardRadius }}>
        <Typography sx={{ ...secondaryFont, fontWeight: 400 }}>Entregar em:</Typography>
        {deliveryAddress ? (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', alignSelf: 'stretch' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationOnIcon sx={{ color: '#F8963E' }} />
              <Typography sx={{ ...secondaryFont, fontWeight: 700 }}>{deliveryAddress}</Typography>
            </Box>
          </Box>
        ) : (
          <Button onClick={() => navigate('/address?from=checkout')}>
            Adicionar endereço de entrega
          </Button>
        )}
      </Card>

      <FieldInfo label="Produtos">
        {order.order_items?.map((orderItem) => (
          <Card key={orderItem.id} sx={{ ...surfaceCardContainer }}>
            <img
              alt="product"
              style={{ width: '80px', height: '80px', borderRadius: '16px', objectFit: 'cover' }}
              src={orderItem.item_photo ? `${apiBaseUrl}${orderItem.item_photo}` : ProductPhotoPlaceholder}
            />

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1, alignSelf: 'stretch' }}>
              <Typography variant="storeName">{orderItem.title}</Typography>

              <Typography
                textAlign="justify"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  wordBreak: 'break-word',
                  flex: 1,
                  fontFamily: 'Nunito',
                  fontSize: '14px',
                  lineHeight: '24px',
                  fontWeight: 400,
                  color: '#546E7A'
                }}
              >
                {orderItem.description}
              </Typography>

              <Typography sx={{ fontFamily: 'Nunito', fontSize: '14px', lineHeight: '24px', fontWeight: 700, color: '#546E7A' }}>
                {currency(orderItem.price * orderItem.quantity)} ({orderItem.quantity} {Number(orderItem.quantity) === 1 ? 'unidade' : 'unidades'})
              </Typography>
            </Box>
          </Card>
        ))}
      </FieldInfo>

      <FieldInfo label="Valores">
        <Card sx={{ ...surfaceCardContainer, flexDirection: 'column' }}>
          <Price label="Subtotal:">
            {currency(order.total_amount)}
          </Price>

          <Price label="Taxa de entrega:">
            {currency(deliveryFee)}
          </Price>

          <Divider sx={{ width: '100%' }} />

          <Price label="Total:">
            {currency(deliveryFee + parseFloat(order.total_amount))}
          </Price>
        </Card>
      </FieldInfo>

      <FieldInfo label="Forma de pagamento">
        <Card sx={{ ...surfaceCardContainer, flexDirection: 'column' }}>
          {paymentMethod?.type === 'pix' && (
            <Typography sx={{ fontFamily: 'Nunito', fontSize: '14px', lineHeight: '24px', fontWeight: 700 }}>
              Pix
            </Typography>
          )}

          {paymentMethod?.type === 'card' && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
              <Box>
                {paymentMethod.brand === 'visa'
                  ? <img src={visa} style={{ width: '60px', height: '40px' }} alt="" />
                  : <img src={master} style={{ width: '60px', height: '40px' }} alt="" />}
              </Box>
              <Box flexGrow={1} ml={2} textAlign="left">
                <Typography sx={{ fontFamily: 'Nunito', fontSize: '14px', lineHeight: '21px', textTransform: 'capitalize' }}>
                  {paymentMethod.brand} • {paymentMethod.funding === 'debit' ? 'Débito' : 'Crédito'}
                </Typography>
                <Typography sx={{ fontFamily: 'Nunito', fontSize: '14px', lineHeight: '21px', color: '#546E7A' }}>
                  **** {paymentMethod.last4}
                </Typography>
              </Box>
            </Box>
          )}
        </Card>
      </FieldInfo>

      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', alignSelf: 'stretch' }}>
        <ButtonBase sx={{ ...columnContainer, ...buttonStyle, color: '#fff', bgcolor: '#F8963E' }}>
          Cancelar pedido
        </ButtonBase>

        <ButtonBase sx={{ ...columnContainer, ...buttonStyle, color: '#fff', bgcolor: '#263238' }}>
          Entrar em contato com a loja
        </ButtonBase>
      </Box>
    </Box>
  );
};

export default OrderDetails;
